import { IconCheckmark } from '@components/icons/checkmark';
import { IconCloseRegular } from '@components/icons/close-regular';
import { IconRatingGood } from '@components/icons/rating-good';
import { IconRatingMedium } from '@components/icons/rating-medium';
import { FilterConfigField } from '@components/table';
import { FilterData } from '@components/table/filters';

import { useAuthenticatedUser } from 'features/authentication';
import { useStore } from 'models/Provider';

const ratingFieldToExternalQuery = (filter: FilterData) => {
  switch (filter.comparer) {
    case 'is':
      if (filter.value === 'withoutmine') return { rating__by_me: 0 };
      if (filter.value === 'withmine') return { rating__by_me: 1 };
      return { rating: filter.value };
    case 'any_of':
      return { rating: filter.value };
    case 'not_is':
    case 'none_of':
      return { rating__exclude: filter.value };
    case 'is_rated_by':
      return { rating__user: filter.value };
    case 'not_is_rated_by':
      return { rating__not_user: filter.value };
    default:
      return {};
  }
};

export const useRatingFilter = (): FilterConfigField => {
  const { t, userStore } = useStore();
  const me = useAuthenticatedUser();

  return {
    id: 'rating',
    label: t('applicants.filters.rating.title'),
    icon: <IconRatingGood />,
    type: 'single_select',
    values: (filter) => {
      if (filter.comparer === 'is_rated_by' || filter.comparer === 'not_is_rated_by') {
        return userStore.list
          .map((user: any) => ({
            value: String(user.id),
            label: user.name,
          }))
          .sort((a: { label: string }, b: { label: string }) => a.label.localeCompare(b.label));
      }

      return [
        { value: 'S', label: t('applicants.filters.rating.S') },
        { value: 'Y', label: t('applicants.filters.rating.Y') },
        { value: 'N', label: t('applicants.filters.rating.N') },
        { value: 'D', label: t('applicants.filters.rating.D') },
        { value: '-', label: t('applicants.filters.rating.-') },
      ];
    },
    quick: [
      {
        id: 'withmine',
        label: t('applicants.filters.rating.withmine'),
        apply: { comparer: 'is_rated_by', value: String(me.id) },
        icon: <IconCheckmark color="green200" />,
      },
      {
        id: 'withoutmine',
        label: t('applicants.filters.rating.withoutmine'),
        apply: { comparer: 'not_is_rated_by', value: String(me.id) },
        icon: <IconRatingMedium color="orange200" />,
      },
      {
        id: 'unrated',
        label: t('applicants.filters.rating.-'),
        apply: { comparer: 'is', value: '-' },
        icon: <IconCloseRegular color="red200" />,
      },
    ],
    allowedComparers: ['is', 'not_is', 'any_of', 'none_of', 'is_rated_by', 'not_is_rated_by'],
    toExternalQuery: ratingFieldToExternalQuery,
  };
};
